import { BLANK_ADDRESS, validateAddress } from '../geo/addressUtils';
import { buildCommuneOptions, buildRegionOptions } from '../geo/geoUtils';
import {
  PICKUP_MODES,
  pickupFormFromWarehouse as scheduleFromWarehouse,
  pickupModeFromValues,
  validatePickupSchedule,
} from '../sellers/sellerFormUtils';

/**
 * The scheduled pickup request form: pick a warehouse, review the address and
 * the hours it brings, choose the day, book it.
 *
 * Only Samex books pickups today. The request keeps a copy of what the user
 * confirmed, not a link to the warehouse: editing the address here changes this
 * pickup only, never the warehouse.
 */

export const PICKUP_COURIER = 'samex';

export const PICKUP_STATUS = {
  PENDING: 'pending',
  CREATED: 'created',
  FAILED: 'failed',
};

export const PICKUP_STATUS_LABELS = {
  [PICKUP_STATUS.PENDING]: 'Pendiente',
  [PICKUP_STATUS.CREATED]: 'Creada',
  [PICKUP_STATUS.FAILED]: 'Fallida',
};

/**
 * Only a pickup the courier accepted takes orders or a manifest; the backend
 * answers 400 for the rest.
 */
export const isPickupUsable = (pickup) => pickup?.status === PICKUP_STATUS.CREATED;

/** The backend's way of saying "all day": one window over the whole day, none after. */
const ALL_DAY_FROM = '00:00';
const ALL_DAY_TO = '23:59';

const isBlank = (value) => !String(value ?? '').trim();
const hhmm = (value) => String(value || '').slice(0, 5);

/** A warehouse whose only window is 00:00–23:59 is collected at any hour. */
const isAllDay = (warehouse) =>
  hhmm(warehouse?.am_pickup_from) === ALL_DAY_FROM &&
  hhmm(warehouse?.am_pickup_to) === ALL_DAY_TO &&
  isBlank(warehouse?.pm_pickup_from) &&
  isBlank(warehouse?.pm_pickup_to);

/**
 * @typedef {object} PickupRequestForm
 * @property {string} courier - The card the form was opened from.
 * @property {number|string} warehouseId
 * @property {import('../geo/addressUtils').AddressValue} address
 * @property {string} references
 * @property {string} requestedDate - `YYYY-MM-DD`
 * @property {{allDay: boolean, amFrom: string, amTo: string, pmFrom: string, pmTo: string}} schedule
 */

/**
 * The courier's own default, 09:00–18:00. A blank schedule read as one window
 * with no hours: "El courier puede pasar entre las y las .".
 */
const defaultSchedule = () => scheduleFromWarehouse(null);

/**
 * @param {string} today - `YYYY-MM-DD`
 * @param {string} [courier]
 */
export const blankPickupRequestForm = (today, courier = PICKUP_COURIER) => ({
  courier,
  warehouseId: '',
  address: { ...BLANK_ADDRESS },
  references: '',
  requestedDate: today,
  schedule: defaultSchedule(),
});

/**
 * The form seeded from a warehouse of `GET pickup-requests/warehouses/`. A
 * warehouse without hours opens on the courier's default of 09:00–18:00, as in
 * the warehouse's own schedule editor.
 *
 * @param {object} warehouse
 * @param {string} today - `YYYY-MM-DD`
 * @param {string} [courier]
 * @returns {PickupRequestForm}
 */
export const pickupRequestFormFromWarehouse = (warehouse, today, courier = PICKUP_COURIER) => ({
  courier,
  warehouseId: warehouse.id,
  address: {
    ...BLANK_ADDRESS,
    street: warehouse.street || '',
    streetNumber: warehouse.street_number || '',
    region: warehouse.region || '',
    commune: warehouse.commune || '',
  },
  references: warehouse.references || '',
  requestedDate: today,
  // All day keeps no windows: switching to one window then starts on 09:00–18:00,
  // not on 00:00–23:59, which the hour list does not even offer.
  schedule: isAllDay(warehouse)
    ? { allDay: true, amFrom: '', amTo: '', pmFrom: '', pmTo: '' }
    : scheduleFromWarehouse({ ...warehouse, all_day_pickup: false }),
});

/**
 * Errors by field, flat: the address keys `AddressFields` reads, `am` and `pm`
 * for `PickupScheduleFields`, plus `warehouseId` and `requestedDate`.
 *
 * @param {PickupRequestForm} form
 * @param {string} today - `YYYY-MM-DD`
 * @returns {Object.<string, string>}
 */
export const validatePickupRequestForm = (form, today) => {
  const errors = {
    ...validateAddress(form.address, { isMexico: false }),
    ...validatePickupSchedule(form.schedule),
  };

  if (isBlank(form.warehouseId)) errors.warehouseId = 'Elige una bodega';
  if (isBlank(form.requestedDate)) errors.requestedDate = 'Campo obligatorio';
  // `YYYY-MM-DD` compares correctly as a string.
  else if (form.requestedDate < today) errors.requestedDate = 'Debe ser hoy o un día futuro';

  return errors;
};

const labelOf = (options, value) => options.find((option) => option.value === value)?.label || value;

/** The four hours of a schedule as the backend takes them. An empty window travels as `""`. */
const schedulePayload = (schedule) => {
  const mode = pickupModeFromValues(schedule);
  const allDay = mode === PICKUP_MODES.ALL_DAY;
  const split = mode === PICKUP_MODES.SPLIT;

  return {
    am_pickup_from: allDay ? ALL_DAY_FROM : schedule.amFrom,
    am_pickup_to: allDay ? ALL_DAY_TO : schedule.amTo,
    pm_pickup_from: split ? schedule.pmFrom : '',
    pm_pickup_to: split ? schedule.pmTo : '',
  };
};

/**
 * The body of `POST pickup-requests/`.
 *
 * The region and the commune travel by name ("Viña del Mar"), not by the
 * catalog's `system_name` the selects hold: the backend stores them as they
 * arrive, and Samex prints them on the pickup.
 *
 * @param {PickupRequestForm} form
 * @param {Array<object>} [regions] - The geo catalog of the country.
 */
export const buildPickupRequestPayload = (
  { courier, warehouseId, address, references, requestedDate, schedule },
  regions = []
) => ({
  courier: courier || PICKUP_COURIER,
  warehouse_id: warehouseId,
  requested_date: requestedDate,
  street: address.street.trim(),
  street_number: address.streetNumber.trim(),
  commune: labelOf(buildCommuneOptions(regions, address.region), address.commune),
  region: labelOf(buildRegionOptions(regions), address.region),
  references: references.trim(),
  ...schedulePayload(schedule),
});

/*
 * A manual pickup: one the courier's support created by phone when its API
 * would not, and that never reaches us by webhook. Only the courier, its id,
 * its date and its hours are stored, and nothing is sent to the courier.
 */

/**
 * @typedef {object} ManualPickupForm
 * @property {string} courier
 * @property {number|string} warehouseId - Only says whose pickup it is, for an
 *   admin; it is not stored.
 * @property {string} externalPickupId
 * @property {string} scheduledDate - `YYYY-MM-DD`
 * @property {{allDay: boolean, amFrom: string, amTo: string, pmFrom: string, pmTo: string}} schedule -
 *   The hours the courier's support agreed to, not the warehouse's.
 */

/**
 * @param {string} today - `YYYY-MM-DD`
 * @param {string} [courier]
 * @returns {ManualPickupForm}
 */
export const blankManualPickupForm = (today, courier = PICKUP_COURIER) => ({
  courier,
  warehouseId: '',
  externalPickupId: '',
  scheduledDate: today,
  schedule: defaultSchedule(),
});

/**
 * @param {ManualPickupForm} form
 * @param {string} today - `YYYY-MM-DD`
 * @param {{needsWarehouse: boolean}} options - An admin picks the warehouse
 *   that says whose pickup it is; a seller has only one company.
 * @returns {Object.<string, string>}
 */
export const validateManualPickupForm = (form, today, { needsWarehouse }) => {
  const errors = validatePickupSchedule(form.schedule);

  if (needsWarehouse && isBlank(form.warehouseId)) errors.warehouseId = 'Elige una bodega';
  if (isBlank(form.externalPickupId)) errors.externalPickupId = 'Campo obligatorio';
  if (isBlank(form.scheduledDate)) errors.scheduledDate = 'Campo obligatorio';
  else if (form.scheduledDate < today) errors.scheduledDate = 'Debe ser hoy o un día futuro';

  return errors;
};

/**
 * The body of `POST pickup-requests/manual/`.
 *
 * @param {ManualPickupForm} form
 */
export const buildManualPickupPayload = ({ courier, warehouseId, externalPickupId, scheduledDate, schedule }) => ({
  courier: courier || PICKUP_COURIER,
  external_pickup_id: externalPickupId.trim(),
  scheduled_date: scheduledDate,
  ...schedulePayload(schedule),
  ...(isBlank(warehouseId) ? {} : { warehouse_id: warehouseId }),
});

/**
 * The hours of a listed pickup in one stretch, `"09:00 - 18:00"`: from when the
 * morning starts to when the last window ends, which is what Samex goes by.
 * Empty for a pickup with no hours on record.
 *
 * @param {object} pickup - As `GET pickup-requests/` answers it.
 * @returns {string}
 */
export const pickupWindowLabel = (pickup) => {
  const from = hhmm(pickup?.am_pickup_from) || hhmm(pickup?.pm_pickup_from);
  const to = hhmm(pickup?.pm_pickup_to) || hhmm(pickup?.am_pickup_to);
  return from && to ? `${from} - ${to}` : '';
};

/** The backend's field names, as the forms call them. */
const FORM_KEYS = {
  warehouse_id: 'warehouseId',
  external_pickup_id: 'externalPickupId',
  scheduled_date: 'scheduledDate',
  street: 'street',
  street_number: 'streetNumber',
  commune: 'commune',
  region: 'region',
  references: 'references',
  requested_date: 'requestedDate',
  am_pickup_from: 'am',
  am_pickup_to: 'am',
  pm_pickup_from: 'pm',
  pm_pickup_to: 'pm',
};

const flatten = (value) => {
  if (Array.isArray(value)) return value.flatMap(flatten);
  return typeof value === 'string' && value.trim() ? [value] : [];
};

/**
 * The `errors` of a 400, keyed the way the form shows them. A key the form has
 * no field for is dropped: the `detail` above the form already says it.
 *
 * @param {Object.<string, string[]|string>} [errors]
 * @returns {Object.<string, string>}
 */
export const pickupFieldErrorsFromApi = (errors) =>
  Object.entries(errors || {}).reduce((fields, [key, value]) => {
    const formKey = FORM_KEYS[key];
    const messages = flatten(value);
    if (!formKey || messages.length === 0) return fields;
    fields[formKey] = fields[formKey] ? `${fields[formKey]} ${messages.join(' ')}` : messages.join(' ');
    return fields;
  }, {});
